import { Vertical } from "@/models/event-item";
import { PassAccessInfo, AccessLevel } from "./PassAccessBadge";

/**
 * All vertical-specific pass types
 */
const VERTICAL_PASSES: Vertical[] = [
  "tech",
  "fashion",
  "music",
  "gaming",
  "culture",
  "film",
  "health",
];

/**
 * Festival-wide passes that grant access to all verticals
 */
const FESTIVAL_PASSES = ["Tribe", "VIP", "Executive"] as const;

/**
 * Given an event's vertical, return which passes grant access and at what level.
 *
 * Access rules based on FAQ:
 * - Vertical-specific passes (e.g., Tech Pass):
 *   - Primary access to events in that vertical
 *   - Secondary access to events in other verticals
 * - Festival-wide passes (Tribe, VIP, Executive):
 *   - Primary access to all events
 * - Day Pass:
 *   - Secondary access to all events
 */
export function getPassAccessForEvent(vertical: Vertical): PassAccessInfo[] {
  const passes: PassAccessInfo[] = [];

  // Festival-wide passes always have primary access
  FESTIVAL_PASSES.forEach((passName) => {
    passes.push({
      passName: `${passName} Pass`,
      accessLevel: "primary",
    });
  });

  // Vertical-specific passes
  VERTICAL_PASSES.forEach((v) => {
    const passName = `${v.charAt(0).toUpperCase() + v.slice(1)} Pass`;
    const accessLevel: AccessLevel = v === vertical ? "primary" : "secondary";
    passes.push({
      passName,
      accessLevel,
    });
  });

  // Day Pass has secondary access to all
  passes.push({
    passName: "Day Pass",
    accessLevel: "secondary",
  });

  return passes;
}

/**
 * Get a simplified view of pass access - just primary passes for the event's vertical
 * Useful for compact badge display
 */
export function getPrimaryPassesForEvent(vertical: Vertical): PassAccessInfo[] {
  const passes: PassAccessInfo[] = [];

  // Festival-wide passes
  FESTIVAL_PASSES.forEach((passName) => {
    passes.push({
      passName: `${passName} Pass`,
      accessLevel: "primary",
    });
  });

  // Vertical-specific pass
  const verticalPassName = `${vertical.charAt(0).toUpperCase() + vertical.slice(1)} Pass`;
  passes.push({
    passName: verticalPassName,
    accessLevel: "primary",
  });

  return passes;
}
